import React, { memo, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  TRANSITION_ACCEPT,
  TRANSITION_CONFIRM_PAYMENT_INSTANT,
  TRANSITION_CONFIRM_PAYMENT_ADMIN_INSTANT,
  TRANSITION_ENQUIRE,
  TRANSITION_MARK_ACCEPTED_AFTER_ONE_HOUR,
  TRANSITION_MARK_ACCEPTED_NON_REFUNDABLE,
  TRANSITION_UNVERIFIED_ACCEPTED_PENDING_STATE_INSTANT,
  TRANSITION_UNVERIFIED_VERIFICATION_ACCEPTED,
  TRANSITION_UNVERIFIED_VERIFICATION_INSTANT_ACCEPTED,
  TRANSITION_ACCEPT_BY_ADMIN,
  txIsAwaitUpdateBookingBeforeDropOff,
  txIsAwaitUpdateBookingBeforePickUp,
  txIsBeingDropOff,
  txIsBeingPickedUp,
  txIsDroppedOff,
  txIsCanceled,
  txIsDelivered,
  txIsCompleted,
  txIsReviewed,
  txWasPickedUp,
  txIsDeclined,
  txIsExpired
} from '../../util/transaction';
import { createResourceLocatorString } from '../../util/routes';
import { txLastTransition } from '../../util/transactionLongTermFirst';
import routeConfiguration from '../../routeConfiguration';
import css from './TripPanel.css';
import editIcon from '../../assets/tripPage/edit-regular.svg';
import addOnsIcon from '../../assets/tripPage/addons.svg';
import dollarIcon from '../../assets/tripPage/trip-cost.svg';
import contractIcon from '../../assets/tripPage/policies.svg';
import helpIcon from '../../assets/tripPage/help.svg';
import beginnersIcon from '../../assets/tripPage/beginner.svg';
import cancelIcon from '../../assets/tripPage/cancel-trip.svg';
import { getEventsByBucket } from '../../util/gtm/gtmCreateProperties';
import { triggerAnalyticsEvent } from '../../util/amplitudeMapEvents';
import { event_trigger_ids } from '../../util/analyticsConstants';
import moment from 'moment';
import { get } from 'lodash';

const tripStarted = (transaction) => {
  if (!transaction || !transaction.booking) return false;

  const tz = get(transaction, 'listing.attributes.publicData.listingTimezone') || "Asia/Singapore";

  const bookingStart = transaction.booking.attributes.displayStart;
  // const bookingEnd = transaction.booking.attributes.displayEnd;

  const startInTz = bookingStart && moment(bookingStart).utc();
  // const endInTz = bookingEnd && moment(bookingEnd).tz(tz);

  const now = moment().utc();

  return now.isAfter(startInTz.clone().subtract(15, 'minutes'));
}

const ImportantLinksSection = ({
  history,
  isCustomer,
  isAccepted,
  onOpenCancelModal,
  onOpenCancelHelpModal,
  isShowCancelLinkCustomer,
  isShowAddonsLinkCustomer,
  transactionId,
  transaction,
  onEditTrip,
  onPoliciesClick,
  isBookingCompleted,
  isGrabTransaction
}) => {

  const lastTransition = txLastTransition(transaction);
  const tripWasAccepted =
    transaction &&
    transaction.attributes &&
    transaction.attributes.transitions
      .map(
        t =>
          t.transition === TRANSITION_MARK_ACCEPTED_NON_REFUNDABLE ||
          t.transition === TRANSITION_UNVERIFIED_ACCEPTED_PENDING_STATE_INSTANT ||
          t.transition === TRANSITION_UNVERIFIED_VERIFICATION_INSTANT_ACCEPTED ||
          t.transition === TRANSITION_MARK_ACCEPTED_AFTER_ONE_HOUR ||
          t.transition === TRANSITION_ACCEPT ||
          t.transition === TRANSITION_ACCEPT_BY_ADMIN ||
          t.transition === TRANSITION_CONFIRM_PAYMENT_INSTANT ||
          t.transition === TRANSITION_CONFIRM_PAYMENT_ADMIN_INSTANT
      )
      .includes(true);

  const isEditLinkVisible = tripWasAccepted &&
    !txIsAwaitUpdateBookingBeforePickUp(transaction) &&
    !txIsAwaitUpdateBookingBeforeDropOff(transaction) &&
    !txIsBeingPickedUp(transaction) &&
    !txIsBeingDropOff(transaction) &&
    !(lastTransition === TRANSITION_ENQUIRE) &&
    !txIsCanceled(transaction) &&
    !txIsDelivered(transaction) &&
    !txIsCompleted(transaction) &&
    !txIsDeclined(transaction) &&
    !txIsExpired(transaction) &&
    !txIsReviewed(transaction) &&
    !txIsDroppedOff(transaction) &&
    !tripStarted(transaction) &&
    !isBookingCompleted && 
    !isGrabTransaction
  ;

  const [eventData, setEventData] = useState(null);

  useEffect(() => {
    let isMounted = true;  // Flag to check if the component is still mounted

    const fetchEventData = async () => {
      try {
        const apiResponse = await getEventsByBucket('trip');
        if (isMounted) {
          setEventData(apiResponse.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching event data:', error);
        }
      }
    };

    fetchEventData();

    return () => {
      isMounted = false;  // Set the flag to false when the component unmounts
    };
  }, []);



  const showCancelLinkForProvider = isAccepted || txIsAwaitUpdateBookingBeforePickUp(transaction)
  const transactionCreatedAt = transaction.attributes.createdAt;
  const dateForTransactionFromOldTransactionProcess = new Date('December 1, 2021 01:00:00');
  const isPickedUpTx = txWasPickedUp(transaction);

  const listOfLinks = isCustomer
    ? [
      {
        id: 1,
        img: addOnsIcon,
        title: <FormattedMessage id="TransactionPanel.addOnsLink" />,
        path: 'AddOnsPage',
        show: isShowAddonsLinkCustomer,
        redirect: true,
      },
      ...(isPickedUpTx ? [] : [{
        id: 2,
        img: editIcon,
        title: isPickedUpTx
          ? <FormattedMessage id="TransactionPanel.editLinkPickedUp" />
          : <FormattedMessage id="TransactionPanel.editLink" />,
        path: 'EditTripPage',
        show: isEditLinkVisible,
        isEdit: true,
        redirect: true,
      }]),
      {
        id: 3,
        img: dollarIcon,
        title: <FormattedMessage id="TransactionPanel.tripCostLink" />,
        path: 'TripDetailsPage',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
      },
      {
        id: 4,
        img: contractIcon,
        title: <FormattedMessage id="TransactionPanel.policiesLink" />,
        path: 'PoliciesPage',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
      },
      {
        id: 5,
        img: helpIcon,
        title: <FormattedMessage id="TransactionPanel.helpLink" />,
        path: '/',
        isModal: true,
        isCancel: false,
        show: true,
      },
      {
        id: 6,
        img: beginnersIcon,
        title: <FormattedMessage id="TransactionPanel.beginLink" />,
        path: 'BeginnerResourcesPage',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
      },
      {
        id: 7,
        img: cancelIcon,
        title: <FormattedMessage id="TransactionPanel.cancelTrip" />,
        path: '/',
        isCancel: true,
        show: tripStarted(transaction) ? false : lastTransition === TRANSITION_ENQUIRE ? false : isShowCancelLinkCustomer,
      },
    ]
    : [
      {
        id: 1,
        img: dollarIcon,
        title: <FormattedMessage id="TransactionPanel.tripDetailsLink" />,
        path: 'TripDetailsPageProvider',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
        isTripDetails: true,
      },
      {
        id: 2,
        img: contractIcon,
        title: <FormattedMessage id="TransactionPanel.policiesLink" />,
        path: 'PoliciesPage',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
      },
      {
        id: 3,
        img: helpIcon,
        title: <FormattedMessage id="TransactionPanel.helpLink" />,
        path: '#',
        isModal: true,
        isCancel: false,
        show: true,
      },
      {
        id: 4,
        img: beginnersIcon,
        title: <FormattedMessage id="TransactionPanel.beginLink" />,
        path: 'BeginnerResourcesPage',
        show: lastTransition !== TRANSITION_ENQUIRE,
        redirect: true,
      },
      {
        id: 5,
        img: cancelIcon,
        title: <FormattedMessage id="TransactionPanel.cancelTrip" />,
        path: '#',
        isCancel: true,
        show: tripStarted(transaction) ? false : lastTransition === TRANSITION_ENQUIRE ? false : showCancelLinkForProvider,
      },
    ];

  const clickLink = async (path, redirect, id, extraParams) => {

    // console.log("Host event is triggered", id, isCustomer);

    if(isCustomer) {

      if (id === 1) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_ADD_ONS_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "Addons",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }

      if (id === 5) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_FAQ_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "FAQ",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }

      if (id === 2) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_MODIFICATION_PAGE_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "Edit Trip",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }

      if (id === 3) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_PRICE_BREAKDOWN_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "Trip Cost Breakdown",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }

      if (id === 4) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_IMPORTANT_DOCUMENTS_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "View Documents",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }

      if (id === 7) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_CANCEL_GUEST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "Cancel Trip",
              page: "TripScreen"
            }
          },
          userId: transaction.customer ? transaction.customer.id.uuid : null,
        });
      }
    }
    else {
      // console.log("Host event is triggered", id);
      // Host Events here
      if (id === 5) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_CANCEL_HOST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: 'Cancel Trip',
              page: 'TripDetailsScreen'
            }
          },
          userId: transaction.provider ? transaction.provider.id.uuid : null,
        });
      }
      if (id === 4) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_IMPORTANT_DOCUMENTS_HOST,
          eventData,
          props: {
            guest: transaction.customer,
            trip: transaction,
            host: transaction.provider,
            ui: {
              button: "Beginner resources",
              page: 'TripDetailsScreen'
            }
          },
          userId: transaction.provider ? transaction.provider.id.uuid : null,
        });
      }

      if (id === 3) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_HELP_TAB_HOST,
          eventData,
          props: {
            guest: transaction.customer,
            trip: transaction,
            host: transaction.provider,
            ui: {
              button: "Need help? Contact us",
              page: 'TripDetailsScreen'
            }
          },
          userId: transaction.provider ? transaction.provider.id.uuid : null,
        });
      }

      if (id === 1) {

        triggerAnalyticsEvent({
          event_id: event_trigger_ids.TRIP_VIEW_PRICE_BREAKDOWN_HOST,
          eventData,
          props: {
            host: transaction.provider,
            guest: transaction.customer,
            trip: transaction,
            ui: {
              button: "Trip Earnings",
              page: 'TripDetailsScreen'
            }
          },
          userId: transaction.provider ? transaction.provider.id.uuid : null,
        });
      }

      // if (id === 5) {
      //
      //   triggerAnalyticsEvent({
      //     event_id: event_trigger_ids.TRIP_VIEW_FAQ_HOST,
      //     eventData,
      //     props: {
      //       guest: transaction.customer,
      //     },
      //     userId: transaction.customer ? transaction.customer.id.uuid : null,
      //   });
      // }



    }


    if(extraParams) {
      return ;
    }

    const routes = routeConfiguration();
    history.push(createResourceLocatorString(path, routes, { id: transactionId }, {}), { startDate: transaction.booking.attributes.start, endDate: transaction.booking.attributes.end });
  };

  const editLinkClick = (path, redirect) => {
    const routes = routeConfiguration();
    history.push(createResourceLocatorString(path, routes, { id: transactionId, action: 'modify-trip' }, {}),);
  };

  const tripDetailsPclick = (path, redirect) => {
    const routes = routeConfiguration();
    history.push(createResourceLocatorString(path, routes, { id: transactionId, action: isCustomer ? 'customer': 'provider' }, {}),);
  };

  return (
    <div className={css.importantLinksContainer}>
      <h3 className={css.importantLinksTitle}>
        <FormattedMessage id="TransactionPanel.importantLinksTitle" />
      </h3>
      {listOfLinks.map(link => {
        return link.show ? (
          <div
            key={link.id}
            className={css.linkContainer}
            onClick={() => (
              link.isModal ? onOpenCancelHelpModal()
                : link.isCancel ? onOpenCancelModal()
                : link.isEdit ? editLinkClick(link.path, link.redirect)
                : link.policies ? onPoliciesClick()
                : link.isTripDetails ? tripDetailsPclick(link.path, link.redirect)
                : clickLink(link.path, link.redirect))}
          >
            <div className={css.imgTextContainer}>
              <div className={css.imgLinkContainer}>
                <img src={link.img} alt="link Icon" className={css.imgLink} />
              </div>
              <p className={css.linkTitle}>{link.title}</p>
            </div>
            <div className={css.arrowLink}></div>
          </div>
        ) : null;
      })}
    </div>
  );
};

export default ImportantLinksSection;
